import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCommunity, REPORT_STATUS } from '../contexts/CommunityContext';
import serverService from '../services/serverService';
import { Card, StatCard, StatusCard } from '../components/common';
import ActivityFeed from '../components/ActivityFeed';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, getAllUsers, getPendingUsers } = useAuth();
  const { getReports } = useCommunity();

  const [stats, setStats] = useState({
    totalUsers: 0,
    pendingUsers: 0,
    familyMembers: 0,
    admins: 0,
    pendingReports: 0
  });
  const [serviceHealth, setServiceHealth] = useState([]);
  const [systemInfo, setSystemInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  // 서비스 목록
  const services = [
    { name: 'Main API', pm2: 'ilouli-main-api', port: 4000 },
    { name: 'Auth', pm2: 'ilouli-auth-api', port: 4001 },
    { name: 'Community', pm2: 'ilouli-community-api', port: 4002 },
    { name: 'Stock', pm2: 'ilouli-stock', port: 4003 },
    { name: 'AI', pm2: 'ilouli-ai-api', port: 4004 },
    { name: 'Admin', pm2: 'ilouli-admin-api', port: 4005 },
    { name: 'Family', pm2: 'ilouli-family-api', port: 3006 },
    { name: 'Lab', pm2: 'ilouli-lab-api', port: 4007 },
    { name: 'DevTeam', pm2: 'ilouli-devteam', port: 3009 },
    { name: 'Markdown', pm2: 'ilouli-markdownblog-api', port: 4009 },
    { name: 'Nexus', pm2: 'ilouli-nexus', port: 4010 },
    { name: 'Invest', pm2: 'ilouli-invest-api', port: 4012 },
  ];

  const loadStats = useCallback(async () => {
    try {
      const [users, pending, reports] = await Promise.all([
        getAllUsers(),
        getPendingUsers(),
        getReports()
      ]);

      const pendingReports = reports?.filter(r => r.status === REPORT_STATUS.PENDING) || [];

      setStats({
        totalUsers: users?.length || 0,
        pendingUsers: pending?.length || 0,
        familyMembers: users?.filter(u => u.tier === 'family').length || 0,
        admins: users?.filter(u => u.tier === 'admin').length || 0,
        pendingReports: pendingReports.length
      });
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, [getAllUsers, getPendingUsers, getReports]);

  const loadServiceHealth = useCallback(async () => {
    try {
      const [pm2Result, systemResult] = await Promise.all([
        serverService.getPM2Processes(),
        serverService.getSystemInfo()
      ]);

      if (pm2Result.success) {
        const pm2List = pm2Result.data || [];
        const healthStatus = services.map(service => {
          const pm2Process = pm2List.find(p => p.name === service.pm2);
          return {
            ...service,
            status: pm2Process?.status === 'online' ? 'online' : 'offline',
            cpu: pm2Process?.cpu || 0,
            memory: pm2Process?.memory || 0,
            uptime: pm2Process?.uptime || null
          };
        });
        setServiceHealth(healthStatus);
      }

      if (systemResult.success) {
        setSystemInfo(systemResult.data);
      }

      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to load service health:', err);
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([loadStats(), loadServiceHealth()]);
      setLoading(false);
    };
    loadAll();

    // 30초마다 서비스 상태 갱신
    const interval = setInterval(loadServiceHealth, 30000);
    return () => clearInterval(interval);
  }, [loadStats, loadServiceHealth]);

  const formatUptime = (ms) => {
    if (!ms) return '-';
    const seconds = Math.floor(ms / 1000);
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const getResourceFillClass = (value) => {
    const numValue = parseFloat(value);
    if (numValue > 80) return 'bg-red-500';
    if (numValue > 50) return 'bg-amber-500';
    return 'bg-green-500';
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="w-10 h-10 border-[3px] border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
        <p className="text-gray-600 text-sm">데이터를 불러오는 중...</p>
      </div>
    );
  }

  const onlineServices = serviceHealth.filter(s => s.status === 'online').length;
  const offlineServices = serviceHealth.filter(s => s.status === 'offline').length;

  return (
    <div className="flex flex-col gap-6 max-w-[1200px] mx-auto max-md:gap-4">
      {/* 인사 헤더 */}
      <div className="flex justify-between items-start mb-2 max-md:flex-col max-md:gap-3">
        <div>
          <h1 className="text-[28px] font-bold text-gray-900 m-0 mb-1 max-md:text-2xl">안녕하세요, {user?.name || '관리자'}님</h1>
          <p className="text-sm text-gray-600 m-0">ilouli.com 관리자 대시보드입니다</p>
        </div>
        {lastUpdated && (
          <span className="text-xs text-gray-400 bg-gray-100 py-1.5 px-3 rounded-full">
            마지막 업데이트: {lastUpdated.toLocaleTimeString('ko-KR')}
          </span>
        )}
      </div>

      {/* 주요 통계 카드 */}
      <div className="grid grid-cols-4 gap-4 max-lg:grid-cols-2 max-md:gap-3 max-sm:grid-cols-1">
        <StatCard
          icon="👥"
          value={stats.totalUsers}
          label="전체 회원"
          clickable
          onClick={() => navigate('/users')}
        />
        <StatCard
          icon="⏳"
          value={stats.pendingUsers}
          label="승인 대기"
          variant={stats.pendingUsers > 0 ? 'warning' : ''}
          clickable
          onClick={() => navigate('/users/pending')}
        />
        <StatCard
          icon="🚨"
          value={stats.pendingReports}
          label="처리 대기 신고"
          variant={stats.pendingReports > 0 ? 'error' : ''}
          clickable
          onClick={() => navigate('/reports')}
        />
        <StatCard
          icon="👨‍👩‍👧‍👦"
          value={stats.familyMembers}
          label="가족 회원"
        />
      </div>

      {/* 시스템 리소스 */}
      {systemInfo && (
        <Card title="시스템 리소스">
          <div className="grid grid-cols-4 gap-4 max-lg:grid-cols-2 max-md:grid-cols-1">
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <span className="text-[13px] text-gray-600">CPU</span>
                <span className="text-[15px] font-semibold text-gray-900">{systemInfo.cpu?.usage || 0}%</span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-sm overflow-hidden">
                <div
                  className={`h-full rounded-sm transition-[width] duration-300 ${getResourceFillClass(systemInfo.cpu?.usage)}`}
                  style={{ width: `${Math.min(systemInfo.cpu?.usage || 0, 100)}%` }}
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <span className="text-[13px] text-gray-600">메모리</span>
                <span className="text-[15px] font-semibold text-gray-900">{systemInfo.memory?.percentage || 0}%</span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-sm overflow-hidden">
                <div
                  className={`h-full rounded-sm transition-[width] duration-300 ${getResourceFillClass(systemInfo.memory?.percentage)}`}
                  style={{ width: `${Math.min(systemInfo.memory?.percentage || 0, 100)}%` }}
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <span className="text-[13px] text-gray-600">디스크</span>
                <span className="text-[15px] font-semibold text-gray-900">{systemInfo.disk?.percentage || 0}%</span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-sm overflow-hidden">
                <div
                  className={`h-full rounded-sm transition-[width] duration-300 ${getResourceFillClass(systemInfo.disk?.percentage)}`}
                  style={{ width: `${Math.min(systemInfo.disk?.percentage || 0, 100)}%` }}
                />
              </div>
            </div>
            <div className="flex flex-col gap-2 justify-center max-md:pt-2 max-md:border-t max-md:border-gray-200">
              <div className="flex justify-between items-center">
                <span className="text-[13px] text-gray-600">업타임</span>
                <span className="text-[15px] font-semibold text-gray-900">{systemInfo.uptime?.formatted || '-'}</span>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* 서비스 상태 요약 + 그리드 */}
      <Card
        title="서비스 상태"
        subtitle={`${onlineServices}/${services.length} 서비스 정상`}
        action={<Link to="/services" className="text-[13px] text-blue-500 no-underline font-medium hover:underline">전체 보기 →</Link>}
      >
        <div className="flex gap-4 mb-4 pb-4 border-b border-gray-200 max-md:flex-col max-md:gap-2">
          <div className={`flex items-center gap-2 py-2 px-4 bg-gray-100 rounded-lg transition-all ${offlineServices === 0 ? 'bg-green-100' : ''}`}>
            <span className={`text-xl font-bold ${offlineServices === 0 ? 'text-green-600' : 'text-green-600'}`}>{onlineServices}</span>
            <span className="text-[13px] text-gray-600">정상</span>
          </div>
          <div className={`flex items-center gap-2 py-2 px-4 bg-gray-100 rounded-lg transition-all ${offlineServices > 0 ? 'bg-red-100' : ''}`}>
            <span className={`text-xl font-bold ${offlineServices > 0 ? 'text-red-600' : 'text-red-600'}`}>{offlineServices}</span>
            <span className="text-[13px] text-gray-600">오프라인</span>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 max-lg:grid-cols-2 max-md:grid-cols-1">
          {serviceHealth.length > 0 ? (
            serviceHealth.map((service) => (
              <StatusCard
                key={service.name}
                name={service.name}
                status={service.status}
                uptime={formatUptime(service.uptime)}
                memory={service.memory}
                cpu={service.cpu}
                onClick={() => navigate('/services')}
              />
            ))
          ) : (
            services.map((service) => (
              <StatusCard
                key={service.name}
                name={service.name}
                status="unknown"
              />
            ))
          )}
        </div>
      </Card>

      {/* 빠른 링크 */}
      <Card title="빠른 링크">
        <div className="grid grid-cols-4 gap-3 max-md:grid-cols-2">
          <Link to="/services" className="flex flex-col items-center gap-2.5 py-5 px-4 bg-gray-50 border border-gray-200 rounded-xl no-underline transition-all hover:bg-gray-100 hover:border-gray-300 hover:-translate-y-0.5">
            <span className="text-[28px] leading-none">🖥️</span>
            <span className="text-[13px] font-medium text-gray-900 text-center">서비스 모니터링</span>
          </Link>
          <Link to="/ai" className="flex flex-col items-center gap-2.5 py-5 px-4 bg-gray-50 border border-gray-200 rounded-xl no-underline transition-all hover:bg-gray-100 hover:border-gray-300 hover:-translate-y-0.5">
            <span className="text-[28px] leading-none">🤖</span>
            <span className="text-[13px] font-medium text-gray-900 text-center">AI 비용 관리</span>
          </Link>
          <Link to="/api-dashboard" className="flex flex-col items-center gap-2.5 py-5 px-4 bg-gray-50 border border-gray-200 rounded-xl no-underline transition-all hover:bg-gray-100 hover:border-gray-300 hover:-translate-y-0.5">
            <span className="text-[28px] leading-none">🔌</span>
            <span className="text-[13px] font-medium text-gray-900 text-center">API 대시보드</span>
          </Link>
          <Link to="/server/monitor" className="flex flex-col items-center gap-2.5 py-5 px-4 bg-gray-50 border border-gray-200 rounded-xl no-underline transition-all hover:bg-gray-100 hover:border-gray-300 hover:-translate-y-0.5">
            <span className="text-[28px] leading-none">⚙️</span>
            <span className="text-[13px] font-medium text-gray-900 text-center">시스템 모니터</span>
          </Link>
        </div>
      </Card>

      {/* 프로젝트 현황 + 실시간 활동 피드 */}
      <div className="grid grid-cols-[1fr_380px] gap-6 max-lg:grid-cols-1">
        <Card
          title="프로젝트 현황"
          action={<Link to="/projects" className="text-[13px] text-blue-500 no-underline font-medium hover:underline">전체 보기 →</Link>}
        >
          <div className="grid grid-cols-6 gap-3 max-lg:grid-cols-3 max-md:grid-cols-2 max-sm:grid-cols-1">
            {[
              { name: 'Main', path: '/projects/main', icon: '🏠', pm2: 'ilouli-main-api' },
              { name: 'Auth', path: '/projects/auth', icon: '🔐', pm2: 'ilouli-auth-api' },
              { name: 'Community', path: '/projects/community', icon: '💬', pm2: 'ilouli-community-api' },
              { name: 'Stock', path: '/projects/stock', icon: '📈', pm2: 'ilouli-stock' },
              { name: 'AI', path: '/projects/ai', icon: '🤖', pm2: 'ilouli-ai-api' },
              { name: 'Family', path: '/projects/family', icon: '👨‍👩‍👧‍👦', pm2: 'ilouli-family-api' },
              { name: 'Lab', path: '/projects/lab', icon: '🔬', pm2: 'ilouli-lab-api' },
              { name: 'DevTeam', path: '/projects/devteam', icon: '👨‍💻', pm2: 'ilouli-devteam' },
              { name: 'Work', path: '/projects/work', icon: '🎬', pm2: 'ilouli-work-backend' },
              { name: 'Claude', path: '/projects/openclaude', icon: '🖥️', pm2: 'ilouli-openclaude' },
              { name: 'Analysis', path: '/projects/stockanalysis', icon: '📊', pm2: 'ilouli-stockanalysis' },
              { name: 'News', path: '/projects/news', icon: '📰', pm2: 'ilouli-news-api' },
              { name: 'Markdown', path: '/projects/markdown', icon: '📝', pm2: 'ilouli-markdownblog-api' },
              { name: 'Nexus', path: '/projects/nexus', icon: '🕸️', pm2: 'ilouli-nexus' },
              { name: 'Invest', path: '/projects/invest', icon: '💹', pm2: 'ilouli-invest-api' },
            ].map(project => {
              const service = serviceHealth.find(s => s.name === project.pm2);
              const status = service?.status || 'unknown';

              return (
                <Link
                  key={project.name}
                  to={project.path}
                  className={`flex flex-col items-center gap-2 py-4 px-3 bg-white border border-gray-200 rounded-xl no-underline transition-all hover:-translate-y-0.5 hover:shadow-md ${status === 'online' ? 'hover:border-green-500' : status === 'offline' ? 'hover:border-red-500' : ''}`}
                >
                  <span className="text-2xl leading-none">{project.icon}</span>
                  <span className="text-[13px] font-semibold text-gray-900">{project.name}</span>
                  <span className={`text-[11px] font-medium py-0.5 px-2 rounded-full ${status === 'online' ? 'bg-green-100 text-green-600' : status === 'offline' ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'}`}>
                    {status === 'online' ? '정상' : status === 'offline' ? '오프라인' : '확인중'}
                  </span>
                </Link>
              );
            })}
          </div>
        </Card>

        {/* 실시간 활동 피드 */}
        <div className="max-lg:-order-1">
          <ActivityFeed maxItems={30} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
